import { useRef, useState } from 'react';
import type {
  AffiliateStockListing,
  CapiProductPage,
  Raffle
} from 'types/capi/pdp';

import HorizontalLine from '~/components/HorizontalLine';
import NotificationCta from '~/components/Pdp/WhereToBuyComingSoon/NotificationCta';
import StickyCta from '~/components/Pdp/WhereToBuyComingSoon/StickyCta';
import StockListing from '~/components/Pdp/WhereToBuyComingSoon/StockListing';
import Title from '~/components/Pdp/WhereToBuyComingSoon/Title';
import CompareRetailersClickEvent from '~/events/CompareRetailersClickEvent';
import usePageData from '~/hooks/usePageData';
import useQsParams from '~/hooks/useQsParams';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { getSortedStockListingsForSize } from '~/utils/listings';

import RaffleListing from './RaffleListing';
import ViewToggle from './ViewToggle';

export enum SelectedView {
  RAFFLES,
  RETAILERS,
  MINIMISED
}

export default function WhereToBuyComingSoon() {
  const { isBot } = useRootData();
  const product = usePageData<CapiProductPage>();
  const { raffle } = useQsParams();

  const hasRaffles = product.stock_listings.raffles.length > 0;

  const [selectedView, setSelectedView] = useState(
    hasRaffles && raffle ? SelectedView.RAFFLES : SelectedView.MINIMISED
  );

  const stockListings = getSortedStockListingsForSize(product);

  const isNotified = useUser().isNotificationSetForProduct(product.legacy_id);

  function onViewToggleClick(newView: SelectedView) {
    setSelectedView(newView);

    if (newView === SelectedView.RETAILERS) {
      return new CompareRetailersClickEvent({ isBot }).send();
    }
  }
  const titleRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <div className="flex w-full flex-col gap-6 md:col-span-full lg:col-span-4">
        <div className="flex w-full flex-col gap-5">
          <span ref={titleRef}>
            <Title />
          </span>
          <div className="flex w-full flex-col items-center gap-4 rounded-lg bg-grey-panel p-4">
            {product.sub_type === 'sneaker' && (
              <NotificationCta product={product} isNotified={isNotified} />
            )}

            <ViewToggle
              stockListings={product.stock_listings}
              selectedView={selectedView}
              setSelectedView={onViewToggleClick}
            />

            {selectedView === SelectedView.RETAILERS && (
              <StockListings listings={stockListings} />
            )}

            {selectedView === SelectedView.RAFFLES && (
              <Raffles raffles={product.stock_listings.raffles} />
            )}
          </div>
        </div>
      </div>
      <StickyCta hideOnElementRef={titleRef} stockListing={stockListings[0]!} />
    </>
  );
}

const StockListings = ({
  listings,
  selectedSize
}: {
  listings: AffiliateStockListing[];
  selectedSize?: string;
}) => {
  return (
    <>
      {listings.map((listing, index) => (
        <StockListing
          key={listing.affiliate.name}
          position={index + 1}
          pagePosition="WTB"
          listing={listing}
          selectedSize={selectedSize}
        />
      ))}
    </>
  );
};

const Raffles = ({ raffles }: { raffles: Raffle[] }) => {
  return (
    <div className="w-full">
      {raffles.map((raffle, index) => (
        <div key={raffle.id}>
          <RaffleListing raffleListing={raffle} buttonPosition={index} />
          <HorizontalLine />
        </div>
      ))}
    </div>
  );
};
